/**
 * leadSource — rastreamento de origem dos leads que clicam no WhatsApp do site.
 *
 * Objetivo: o Ziro CRM identificar de onde veio cada lead e, no caso de anúncio,
 * qual campanha. O Ziro (edge receive-message) lê DOIS sinais no TEXTO da mensagem:
 *   1) Parâmetros UTM embutidos (`utm_source=...&utm_medium=...&utm_campaign=...`).
 *      Têm PRECEDÊNCIA: se houver UTM, a origem vira "Google Ads" e a campanha é gravada.
 *   2) Marcador entre colchetes (ex.: `[site]`), usado quando NÃO há UTM (visita orgânica).
 *
 * Por isso: visita orgânica manda `[site]`; visita de anúncio manda o bloco `utm_*`
 * (sem `[site]`, que confundiria). O público (ótica B2B x consumidor B2C) vai sempre
 * como marcador legível `[otica]`/`[consumidor]`, definido pela agência via `?publico=`
 * na URL do anúncio.
 *
 * Os parâmetros são capturados na PRIMEIRA visita e guardados na sessão, porque o
 * usuário pode navegar (e perder os parâmetros da URL) antes de clicar no WhatsApp.
 */

export const WHATSAPP_NUMBER = "5531985015555";

const STORAGE_KEY = "ao_lead_source";
const UTM_KEYS = ["source", "medium", "campaign", "term", "content"] as const;

export interface LeadSource {
  utm: Partial<Record<(typeof UTM_KEYS)[number], string>>;
  gclid?: string;
  publico?: string; // "otica" | "consumidor" (definido pela agência na URL do anúncio)
}

function sanitize(value: string): string {
  // O Ziro lê utm com a regex /utm_(\w+)=([^&\s]+)/ — espaço corta o valor.
  return value.replace(/\s+/g, "-").trim();
}

function readFromUrl(): LeadSource {
  const out: LeadSource = { utm: {} };
  if (typeof window === "undefined") return out;
  const q = new URLSearchParams(window.location.search);
  for (const k of UTM_KEYS) {
    const v = q.get(`utm_${k}`);
    if (v) out.utm[k] = sanitize(v);
  }
  const gclid = q.get("gclid");
  if (gclid) out.gclid = sanitize(gclid);
  // Público: 1º o parâmetro ?publico= (definido pela agência no anúncio);
  // 2º derivado da landing page dedicada (/para-sua-otica = B2B, /para-voce = B2C).
  const publicoParam = (q.get("publico") || "").toLowerCase();
  const path = (window.location.pathname || "").toLowerCase();
  if (publicoParam === "otica" || publicoParam === "consumidor") {
    out.publico = publicoParam;
  } else if (path.startsWith("/para-sua-otica")) {
    out.publico = "otica";
  } else if (path.startsWith("/para-voce")) {
    out.publico = "consumidor";
  }
  return out;
}

function load(): LeadSource {
  if (typeof window === "undefined") return { utm: {} };
  try {
    const raw = window.sessionStorage.getItem(STORAGE_KEY);
    return raw ? (JSON.parse(raw) as LeadSource) : { utm: {} };
  } catch {
    return { utm: {} };
  }
}

function hasAny(src: LeadSource): boolean {
  return Object.keys(src.utm).length > 0 || !!src.gclid || !!src.publico;
}

/**
 * Captura os parâmetros da URL atual e mescla na sessão (não sobrescreve com vazio).
 * Chamar no início do app e a cada mudança de rota.
 */
export function captureLeadSource(): void {
  if (typeof window === "undefined") return;
  const fromUrl = readFromUrl();
  if (!hasAny(fromUrl)) return; // nada novo na URL, mantém o que já estava
  const merged = load();
  merged.utm = { ...merged.utm, ...fromUrl.utm };
  if (fromUrl.gclid) merged.gclid = fromUrl.gclid;
  if (fromUrl.publico) merged.publico = fromUrl.publico;
  try {
    window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(merged));
  } catch {
    /* sessão indisponível: segue sem persistir */
  }
}

/** Monta o bloco de origem (UTM ou [site]) + marcador de público, em texto puro. */
function buildOriginTag(src: LeadSource): string {
  const utm = { ...src.utm };
  // Auto-tagging do Google (só gclid, sem utm): sintetiza origem paga p/ o Ziro classificar.
  if (src.gclid && !utm.source) {
    utm.source = "google";
    utm.medium = utm.medium || "cpc";
  }
  const utmPairs = (Object.keys(utm) as (typeof UTM_KEYS)[number][])
    .filter((k) => utm[k])
    .map((k) => `utm_${k}=${utm[k]}`);
  const hasUtm = utmPairs.length > 0;

  const audience = src.publico ? `[${src.publico}]` : "";

  if (hasUtm) {
    return [audience, utmPairs.join("&")].filter(Boolean).join(" ");
  }
  return ["[site]", audience].filter(Boolean).join(" ");
}

/** Acrescenta o rastreamento de origem a uma mensagem base. */
export function withLeadSource(baseMessage: string): string {
  const tag = buildOriginTag(load());
  return `${baseMessage.trim()} ${tag}`.trim();
}

/** URL completa do WhatsApp já com a mensagem e o rastreamento de origem. */
export function buildWhatsAppUrl(baseMessage: string, phone: string = WHATSAPP_NUMBER): string {
  return `https://wa.me/${phone}?text=${encodeURIComponent(withLeadSource(baseMessage))}`;
}
